/**
 * Lorem Ipsum generator — pure, seeded (deterministic).
 *
 * `generateLorem(opts, seed)` returns the same text for the same seed, so the
 * component can seed a `useState` initializer with a fixed seed and render
 * identical markup on server and client (no hydration mismatch). A "regenerate"
 * action bumps the seed.
 */

/** Small, fast, seeded PRNG (mulberry32) → values in [0, 1). */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export const LOREM_WORDS = [
  "lorem",
  "ipsum",
  "dolor",
  "sit",
  "amet",
  "consectetur",
  "adipiscing",
  "elit",
  "sed",
  "do",
  "eiusmod",
  "tempor",
  "incididunt",
  "ut",
  "labore",
  "et",
  "dolore",
  "magna",
  "aliqua",
  "enim",
  "ad",
  "minim",
  "veniam",
  "quis",
  "nostrud",
  "exercitation",
  "ullamco",
  "laboris",
  "nisi",
  "aliquip",
  "ex",
  "ea",
  "commodo",
  "consequat",
  "duis",
  "aute",
  "irure",
  "in",
  "reprehenderit",
  "voluptate",
  "velit",
  "esse",
  "cillum",
  "eu",
  "fugiat",
  "nulla",
  "pariatur",
  "excepteur",
  "sint",
  "occaecat",
  "cupidatat",
  "non",
  "proident",
  "sunt",
  "culpa",
  "qui",
  "officia",
  "deserunt",
  "mollit",
  "anim",
  "id",
  "est",
  "laborum",
  "perspiciatis",
  "unde",
  "omnis",
  "iste",
  "natus",
  "error",
  "voluptatem",
  "accusantium",
  "doloremque",
  "laudantium",
  "totam",
  "rem",
];

const LEAD_WORDS = [
  "lorem",
  "ipsum",
  "dolor",
  "sit",
  "amet",
  "consectetur",
  "adipiscing",
  "elit",
];
const LEAD_SENTENCE =
  "Lorem ipsum dolor sit amet, consectetur adipiscing elit.";

export type LoremType = "paragraphs" | "sentences" | "words";

export interface LoremOptions {
  type: LoremType;
  count: number;
  startWithLorem: boolean;
}

function randInt(rng: () => number, min: number, max: number): number {
  return min + Math.floor(rng() * (max - min + 1));
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function makeSentence(rng: () => number): string {
  const n = randInt(rng, 6, 14);
  const words: string[] = [];
  for (let i = 0; i < n; i++) {
    words.push(LOREM_WORDS[Math.floor(rng() * LOREM_WORDS.length)]);
  }
  // Insert a comma roughly mid-sentence for natural rhythm.
  if (n > 6) words[Math.floor(n / 2)] += ",";
  return `${capitalize(words.join(" "))}.`;
}

export function generateLorem(opts: LoremOptions, seed: number): string {
  const rng = mulberry32(seed);
  const count = Math.max(1, Math.min(Math.trunc(opts.count) || 1, 100));

  if (opts.type === "words") {
    const words: string[] = [];
    for (let i = 0; i < count; i++) {
      words.push(
        opts.startWithLorem && i < LEAD_WORDS.length
          ? LEAD_WORDS[i]
          : LOREM_WORDS[Math.floor(rng() * LOREM_WORDS.length)],
      );
    }
    return capitalize(words.join(" "));
  }

  if (opts.type === "sentences") {
    const sentences: string[] = [];
    for (let i = 0; i < count; i++) sentences.push(makeSentence(rng));
    if (opts.startWithLorem) sentences[0] = `${LEAD_SENTENCE} ${sentences[0]}`;
    return sentences.join(" ");
  }

  const paragraphs: string[] = [];
  for (let i = 0; i < count; i++) {
    const ns = randInt(rng, 3, 6);
    const sentences: string[] = [];
    for (let j = 0; j < ns; j++) sentences.push(makeSentence(rng));
    paragraphs.push(sentences.join(" "));
  }
  if (opts.startWithLorem) {
    paragraphs[0] = `${LEAD_SENTENCE} ${paragraphs[0]}`;
  }
  return paragraphs.join("\n\n");
}
